import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { Event, Media, People as ConPeople, Post, Tool } from "../types/content";
import { Topic } from "../types/category";
import { Entry } from "../types/directory";
import { identifyUser, trackLogout } from "../utils/analytics";
import { extractApiError } from "../utils/validation";

export interface UserState {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  username: string;
  auth: {
    groups: [string, string][];
    permissions: [string, string][];
  };
  profile: {
    id: string;
    created_date: string;
    updated_date: string | null;
    location: string | null;
    favourite_directory_entries: Entry[];
    favourite_posts: Post[];
    favourite_people: ConPeople[];
    favourite_events: Event[];
    favourite_tools: Tool[];
    favourite_media: Media[];
    favourite_topics: Topic[];
    favourite_collections: any[];
    is_verified: boolean;
  };
}

// Define a type for the slice state
export interface AuthState {
  access: string | null;
  refresh: string | null;
  isAuthenticated: boolean;
  waiting: boolean;
  loading: boolean;
  user: UserState | null;
  email: {
    verification: {
      loading: boolean;
      sent: boolean;
    };
    referral: {
      loading: boolean;
      sent: boolean;
    };
    contact: {
      loading: boolean;
      sent: boolean;
    };
    recovery: {
      loading: boolean;
      verified: boolean;
      complete: boolean;
    };
  };
  favourites: {
    id: string | null;
    loading: boolean;
  }
  /**
   * Set when something on the page needs the visitor to sign in -- the
   * members-only gate on a card or a collection, for instance.
   *
   * There is no /login route (the form lives in a modal the navbar owns), so
   * a control deep in the tree cannot open it directly. It raises this flag
   * instead; the navbar opens the modal and lowers it again.
   */
  loginPromptOpen: boolean;
}

// Define the initial state using that type
const initialState: AuthState = {
  access: localStorage.getItem("access"),
  refresh: localStorage.getItem("refresh"),
  isAuthenticated: false,
  waiting: false,
  loading: true,
  user: null,
  email: {
    verification: {
      loading: false,
      sent: false,
    },
    referral: {
      loading: false,
      sent: false,
    },
    contact: {
      loading: false,
      sent: false,
    },
    recovery: {
      loading: false,
      verified: false,
      complete: false,
    },
  },
  favourites: {
    id: null,
    loading: false,
  },
  loginPromptOpen: false,
};

//////////////////////////////
//////// Async Thunks ////////
//////////////////////////////

// Get auth
export const getAuth = createAsyncThunk(
  "auth/get",
  async (arg, { getState, rejectWithValue }) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Retrieve auth data
      let res = await axios.get(`/api/auth`, config);
      if (res.status === 200) {
        // Identified here rather than in the reducer: reducers stay pure, and this
        // is the one path every authenticated session passes through — fresh login
        // and token-refreshed return visit alike.
        identifyUser(res.data);
        return { data: res.data, status: res.status };
      }

      // If not successful, try to refresh credentials
      const refreshConfig = { headers: { "Content-Type": "application/json" } };
      const body = { refresh: localStorage.refresh };
      res = await axios.post(`/api/auth/login/refresh`, body, refreshConfig);

      // Store new access token
      localStorage.setItem("access", res.data.access);

      // Try to get auth again
      const newConfig = {
        headers: { Authorization: `Bearer ${res.data.access}` },
      };
      res = await axios.get(`/api/auth`, newConfig);
      identifyUser(res.data);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      } else {
        return rejectWithValue(err.message);
      }
    }
  }
);

// Login
export const login = createAsyncThunk(
  "auth/post/login",
  async (
    obj: { username: string; password: string },
    { getState, rejectWithValue }
  ) => {
    try {
      const { username, password } = obj;
      const config = { headers: { "Content-Type": "application/json" } };
      const body = JSON.stringify({ username, password });
      const res = await axios.post(`/api/auth/login`, body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      // Keep the status so the form can tell "wrong credentials" (401) apart from
      // "too many attempts" (429) or the server being down.
      if (err.response) {
        return rejectWithValue({ status: err.response.status, data: err.response.data });
      }
      return rejectWithValue({ status: null, data: err.message });
    }
  }
);

// Logout
export const logout = createAsyncThunk(
  "auth/post/logout",
  async (arg, { getState, rejectWithValue }) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${auth.access}`,
        },
      };
      const res = await axios.post(`/api/auth/logout`, null, config);

      localStorage.removeItem("access");
      localStorage.removeItem("refresh");
      // Paired with identifyUser in getAuth: unlink the browser from this account
      // so a shared machine does not attribute the next session to it.
      trackLogout();
      return { data: res.data, status: res.status };
    } catch (err: any) {
      console.log(err);
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  }
);

// Register
export const register = createAsyncThunk(
  "auth/post/register",
  async (
    obj: {
      firstName: string;
      lastName: string;
      email: string;
      username: string;
      currentPassword: string;
      confirmPassword: string;
      referralId: string | null;
      agreeMarketing: boolean;
      agreeTerms: boolean;
    },
    { getState, rejectWithValue }
  ) => {
    try {
      const config = { headers: { "Content-Type": "application/json" } };
      const body = JSON.stringify({
        first_name: obj.firstName,
        last_name: obj.lastName,
        email: obj.email,
        username: obj.username,
        current_password: obj.currentPassword,
        confirm_password: obj.confirmPassword,
        update_password: "",
        agree_terms: obj.agreeTerms,
        agree_marketing: obj.agreeMarketing,
        location: null,
      });

      let endpoint = `/api/auth/register`;
      if (obj.referralId !== null && obj.referralId.length > 0)
        endpoint += `?referral_id=${obj.referralId}`;
      const res = await axios.post(endpoint, body, config);

      return { data: res.data, status: res.status };
    } catch (err: any) {
      // Reject with the response body rather than the Axios error: the body carries
      // the per-field serializer errors the form needs, and the error object itself
      // is not serialisable into the store.
      if (err.response) {
        return rejectWithValue({ status: err.response.status, data: err.response.data });
      }
      return rejectWithValue({ status: null, data: err.message });
    }
  }
);

// Update Profile
export const updateProfile = createAsyncThunk(
  "auth/post/update-profile",
  async (
    obj: {
      firstName: string;
      lastName: string;
      email: string;
      username: string;
      currentPassword: string;
      updatePassword: string;
      confirmPassword: string;
      location: string | null;
      favouriteTopics: string[];
    },
    { getState, rejectWithValue }
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = {
        headers: {
          Authorization: `Bearer ${auth.access}`,
          "Content-Type": "application/json",
        },
      };

      const body = JSON.stringify({
        first_name: obj.firstName,
        last_name: obj.lastName,
        email: obj.email,
        username: obj.username,
        current_password: obj.currentPassword,
        update_password: obj.updatePassword,
        confirm_password: obj.confirmPassword,
        location: obj.location,
        following_people: [],
        favourite_posts: [],
        favourite_peoples: [],
        favourite_events: [],
        favourite_tools: [],
        favourite_media: [],
        favourite_topics: obj.favouriteTopics
      });

      const res = await axios.put(`/api/auth`, body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err);
      }
    }
  }
);

// Send verification email
export const sendVerificationEmail = createAsyncThunk(
  "auth/post/verification/email",
  async (obj: { email: string }, { getState, rejectWithValue }) => {
    try {
      const { email } = obj;
      const config = { headers: { "Content-Type": "application/json" } };
      const body = JSON.stringify({ email });
      const res = await axios.post(
        `/api/auth/verification`,
        body,
        config
      );
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  }
);

// Confirm account verification
export const confirmAccountVerification = createAsyncThunk(
  "auth/get/verification/confirm",
  async (
    obj: {
      profileId: string | undefined;
      referralId: string | null;
    },
    { getState, rejectWithValue }
  ) => {
    try {
      let endpoint = `/api/auth/verification/confirm/${obj.profileId}`;
      if (obj.referralId !== null && obj.referralId.length > 0)
        endpoint += `?referral_id=${obj.referralId}`;
      const res = await axios.get(endpoint);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  }
);

// Delete account
export const deleteAccountPermanently = createAsyncThunk(
  "auth/delete/account",
  async (args, { getState, rejectWithValue }) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const res = await axios.delete(`/api/auth`, {
        headers: { Authorization: `Bearer ${auth.access}` },
      });
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data) {
        return rejectWithValue({
          data: err.response.data,
          status: err.response.status,
        });
      } else {
        return rejectWithValue(err.message);
      }
    }
  }
);

// Send contact us email
export const sendContactUsEmail = createAsyncThunk(
  "auth/post/contact-us/email",
  async (
    obj: {
      name: string;
      email: string;
      subject: string;
      message: string;
    },
    { getState, rejectWithValue }
  ) => {
    try {
      // Configure authorization header with user's token
      const config = { headers: { "Content-Type": "application/json" } };

      const { name, email, subject, message } = obj;
      const body = JSON.stringify({ name, email, subject, message });

      const res = await axios.post(
        `/api/auth/contact`,
        body,
        config
      );
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  }
);

// Forgot my password
export const submitEmailToRecoverPassword = createAsyncThunk(
  "auth/post/forgot-my-password",
  async (obj: { email: string }, { getState, rejectWithValue }) => {
    try {
      const { email } = obj;
      const config = { headers: { "Content-Type": "application/json" } };
      const body = JSON.stringify({ email });

      let endpoint = `/api/auth/forgot-my-password`;
      const res = await axios.post(endpoint, body, config);

      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  }
);

// Verify password recovery session
export const verifyPasswordRecoverySession = createAsyncThunk(
  "auth/forgot-my-password/recovery/get",
  async (
    obj: { profileId: string | null; sessionId: string | null },
    { getState, rejectWithValue }
  ) => {
    try {
      // Verify profile ID and session ID
      const config = {};
      let res = await axios.get(
        `/api/auth/forgot-my-password/verify?profile=${obj.profileId}&session=${obj.sessionId}`,
        config
      );
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  }
);

// Update password by recovery
export const updatePasswordByRecovery = createAsyncThunk(
  "auth/post/forgot-my-password/recovery",
  async (
    obj: {
      profileId: string | null;
      password: string;
      confirmPassword: string;
      sessionId: string | null;
    },
    { getState, rejectWithValue }
  ) => {
    try {
      const { profileId, password, confirmPassword, sessionId } = obj;
      const config = { headers: { "Content-Type": "application/json" } };
      const body = JSON.stringify({
        profile: profileId,
        new_password: password,
        confirm_password: confirmPassword,
        session: sessionId,
      });

      let endpoint = `/api/auth/forgot-my-password/recover`;
      const res = await axios.post(endpoint, body, config);

      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      } else {
        return rejectWithValue(err.message);
      }
    }
  }
);

// Add to Favourites
export const addToFavourites = createAsyncThunk(
  "content/favourites/add",
  async (
    obj: {
      id: string;
      type: string;
    },
    { getState, rejectWithValue }
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const headers = {
        Authorization: `Bearer ${auth.access}`,
        "Content-Type": "application/json",
      };

      const body = JSON.stringify({
        id: obj.id,
        object_type: obj.type,
      });

      const res = await axios.put("/api/content/favourite/add", body, { headers });
      return { data: res.data, status: res.status };
    } catch (err: any) {
      return rejectWithValue(extractApiError(err, err?.message || ""));
    }
  }
);

// Remove from Favourites
export const removeFromFavourites = createAsyncThunk(
  "content/favourites/remove",
  async (
    obj: {
      id: string;
      type: string;
    },
    { getState, rejectWithValue }
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const headers = {
        Authorization: `Bearer ${auth.access}`,
        "Content-Type": "application/json",
      };

      const body = JSON.stringify({
        id: obj.id,
        object_type: obj.type,
      });

      const res = await axios.put("/api/content/favourite/remove", body, { headers });
      return { data: res.data, status: res.status };
    } catch (err: any) {
      return rejectWithValue(extractApiError(err, err?.message || ""));
    }
  }
);

/////////////////////////////
//////// Redux slice ////////
/////////////////////////////

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    openLoginPrompt: (state) => {
      state.loginPromptOpen = true;
    },
    closeLoginPrompt: (state) => {
      state.loginPromptOpen = false;
    },
  },
  extraReducers: (builder) => {
    builder

      // Get Auth
      .addCase(getAuth.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAuth.fulfilled, (state, action) => {
        state.isAuthenticated = true;
        state.loading = false;
        state.user = action.payload.data;
      })
      .addCase(getAuth.rejected, (state) => {
        state.loading = false;
        state.user = null;
        state.access = null;
        state.refresh = null;
        localStorage.removeItem("access");
        localStorage.removeItem("refresh");
      })

      // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.access = action.payload.data.access;
        state.refresh = action.payload.data.refresh;
        state.isAuthenticated = true;

        // Set auth tokens in local storage
        localStorage.setItem("access", action.payload.data.access);
        localStorage.setItem("refresh", action.payload.data.refresh);
      })
      .addCase(login.rejected, (state) => {
        localStorage.removeItem("access");
        localStorage.removeItem("refresh");
        state.user = null;
        state.access = null;
        state.refresh = null;
        state.isAuthenticated = false;
        state.loading = false;
      })

      // Logout
      .addCase(logout.pending, (state) => {
        state.loading = true;
        state.waiting = true;
      })
      .addCase(logout.fulfilled, (state) => {
        localStorage.removeItem("access");
        localStorage.removeItem("refresh");
        state.user = null;
        state.access = null;
        state.refresh = null;
        state.isAuthenticated = false;
        state.loading = false;
        state.waiting = false;
      })
      .addCase(logout.rejected, (state) => {
        return state;
      })

      // Register
      .addCase(register.pending, (state) => {
        state.loading = true;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
      })
      .addCase(register.rejected, (state) => {
        state.loading = false;
        state.isAuthenticated = false;
      })

      // Update Profile
      .addCase(updateProfile.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.user = action.payload.data;
        state.loading = false;
      })
      .addCase(updateProfile.rejected, (state) => {
        state.loading = false;
      })

      // Send verification email
      .addCase(sendVerificationEmail.pending, (state) => {
        state.email.verification.loading = true;
        state.email.verification.sent = false;
      })
      .addCase(sendVerificationEmail.fulfilled, (state, action) => {
        state.email.verification.loading = false;
        state.email.verification.sent = true;
      })
      .addCase(sendVerificationEmail.rejected, (state) => {
        state.email.verification.loading = false;
        state.email.verification.sent = false;
      })

      // Confirm account verification
      .addCase(confirmAccountVerification.pending, (state) => {
        state.loading = true;
      })
      .addCase(confirmAccountVerification.fulfilled, (state, action) => {
        state.isAuthenticated = false;
        state.loading = false;
        state.user = action.payload.data.user;
        state.access = action.payload.data.access;
        state.refresh = action.payload.data.refresh;
        state.isAuthenticated = true;

        // Set auth tokens in local storage
        localStorage.setItem("access", action.payload.data.access);
        localStorage.setItem("refresh", action.payload.data.refresh);
      })
      .addCase(confirmAccountVerification.rejected, (state) => {
        state.loading = false;
      })

      // Delete account permanently
      .addCase(deleteAccountPermanently.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteAccountPermanently.fulfilled, (state, action) => {
        localStorage.removeItem("access");
        localStorage.removeItem("refresh");
        state.user = null;
        state.access = null;
        state.refresh = null;
        state.isAuthenticated = false;
        state.loading = false;
      })
      .addCase(deleteAccountPermanently.rejected, (state) => {
        state.loading = false;
      })

      // Send contact us email
      .addCase(sendContactUsEmail.pending, (state) => {
        state.email.contact.loading = true;
        state.email.contact.sent = false;
      })
      .addCase(sendContactUsEmail.fulfilled, (state, action) => {
        state.email.contact.loading = false;
        state.email.contact.sent = true;
      })
      .addCase(sendContactUsEmail.rejected, (state) => {
        state.email.contact.loading = false;
        state.email.contact.sent = false;
      })

      // Forgot my password
      .addCase(submitEmailToRecoverPassword.pending, (state) => {
        state.email.recovery.loading = true;
      })
      .addCase(submitEmailToRecoverPassword.fulfilled, (state, action) => {
        state.email.recovery.loading = false;
      })
      .addCase(submitEmailToRecoverPassword.rejected, (state) => {
        state.email.recovery.loading = false;
      })

      // Verify password recovery session
      .addCase(verifyPasswordRecoverySession.pending, (state) => {
        state.email.recovery.loading = true;
        state.email.recovery.verified = false;
      })

      .addCase(verifyPasswordRecoverySession.fulfilled, (state, action) => {
        state.email.recovery.loading = false;
        state.email.recovery.verified = true;
      })

      .addCase(verifyPasswordRecoverySession.rejected, (state) => {
        state.email.recovery.loading = false;
        state.email.recovery.verified = false;
      })

      // Update password by recovery
      .addCase(updatePasswordByRecovery.pending, (state) => {
        state.email.recovery.loading = true;
        state.email.recovery.complete = false;
      })

      .addCase(updatePasswordByRecovery.fulfilled, (state, action) => {
        state.email.recovery.loading = false;
        state.email.recovery.complete = true;

        state.isAuthenticated = false;
        state.loading = false;
        state.user = action.payload.data.user;
        state.access = action.payload.data.access;
        state.refresh = action.payload.data.refresh;
        state.isAuthenticated = true;

        // Set auth tokens in local storage
        localStorage.setItem("access", action.payload.data.access);
        localStorage.setItem("refresh", action.payload.data.refresh);
      })

      .addCase(updatePasswordByRecovery.rejected, (state, action) => {
        state.email.recovery.loading = false;
        state.email.recovery.complete = false;
      })

      // Add to Favourites
      .addCase(addToFavourites.pending, (state, action) => {
        state.favourites.id = action.meta.arg.id;
        state.favourites.loading = true;
      })
      .addCase(addToFavourites.fulfilled, (state, action) => {
        state.user = action.payload.data;
        state.favourites.loading = false;
        state.favourites.id = null; // Reset after successful addition
      })
      .addCase(addToFavourites.rejected, (state, action) => {
        state.favourites.loading = false;
        state.favourites.id = null; // Reset on error
      })

      // Remove from Favourites
      .addCase(removeFromFavourites.pending, (state, action) => {
        state.favourites.id = action.meta.arg.id;
        state.favourites.loading = true;
      })
      .addCase(removeFromFavourites.fulfilled, (state, action) => {
        state.user = action.payload.data;
        state.favourites.loading = false;
        state.favourites.id = null; // Reset after successful removal
      })
      .addCase(removeFromFavourites.rejected, (state, action) => {
        state.favourites.loading = false;
        state.favourites.id = null; // Reset on error
      })
  },
});

export const { openLoginPrompt, closeLoginPrompt } = authSlice.actions;

export default authSlice.reducer;
